import Phaser from 'phaser';
import { runtimeBus, type DangerLevel, type UiAction } from '../../app/runtimeBus';
import { AudioDirector } from '../../audio/AudioDirector';
import {
  DANGER_DURATION_MS,
  FIELD_HEIGHT,
  FIELD_WIDTH,
  FIXED_STEP_MS,
  RENDER_RESOLUTION_SCALE,
  WORLD_GRAVITY,
  VIEW_WIDTH,
  VIEW_HEIGHT,
  VIEW_SIDE,
} from '../../config/gameplay';
import { getCharacterAtStage } from '../../game/content/characters';
import { getGameMode, getModePhase, type GameModeId } from '../../game/content/modes';
import { InputController } from '../../game/input/InputController';
import { AutoPlayController } from '../../game/autoplay/AutoPlayController';
import { GameSession } from '../../game/simulation/GameSession';
import type { CardUsePreparation, CardUseRequest, PhysicsStepObservation, SessionEvent, SessionResult, SessionState } from '../../game/simulation/types';
import { RecordStore, type GameRecord } from '../../game/storage/recordStore';
import { SceneBridge } from '../adapters/SceneBridge';
import { DropFlowController } from '../controllers/DropFlowController';
import { BodyFactory } from '../physics/BodyFactory';
import { CollisionCoordinator } from '../physics/CollisionCoordinator';
import { PresentationDirector } from '../presentation/PresentationDirector';
import { CameraFraming } from '../presentation/CameraFraming';

export class GameScene extends Phaser.Scene {
  private economyModal = false;
  private economyResumeGeneration: number | null = null;
  private session!: GameSession;
  private state!: SessionState;
  private bridge!: SceneBridge;
  private dropFlow!: DropFlowController;
  private bodies!: BodyFactory;
  private collisions!: CollisionCoordinator;
  private presentation!: PresentationDirector;
  private audio!: AudioDirector;
  private controls!: InputController;
  private autoplay!: AutoPlayController;
  private resumeAutoAfterFocus = false;
  private latestStep = 0;
  private gameplayTimeMs = 0;
  private unsubscribeUi: (() => void) | null = null;
  private uninstallDebug: (() => void) | null = null;
  private debugLoadGeneration = 0;
  private recordStore!: RecordStore;
  private record!: GameRecord;
  private focusSuspended = false;
  private dangerLevel: DangerLevel = 'safe';
  private readonly framing = new CameraFraming();
  private readonly pendingCardUses: CardUseRequest[] = [];

  public constructor() {
    super('GameScene');
  }

  public create(): void {
    this.syncRenderCamera();

    // Finite posts end at y=0; there is no ceiling or invisible wall above them.
    this.matter.add.rectangle(-12, FIELD_HEIGHT / 2, 24, FIELD_HEIGHT, { isStatic: true });
    this.matter.add.rectangle(FIELD_WIDTH + 12, FIELD_HEIGHT / 2, 24, FIELD_HEIGHT, { isStatic: true });
    this.matter.add.rectangle(FIELD_WIDTH / 2, FIELD_HEIGHT + 12, FIELD_WIDTH + 48, 24, { isStatic: true });
    this.matter.world.setGravity(0, WORLD_GRAVITY);
    this.recordStore = new RecordStore(window.localStorage);
    this.record = this.recordStore.load();
    runtimeBus.sendRecord(this.record);
    runtimeBus.sendDanger('safe');
    this.session = new GameSession({
      seed: readSeed(),
      createRunId: () => crypto.randomUUID(),
      observerReached: this.record.observerReached,
    });
    this.state = this.session.getState();
    this.bodies = new BodyFactory(this);
    this.audio = new AudioDirector(this);
    this.presentation = new PresentationDirector(this, this.bodies, (id, outcome) => {
      this.bridge.settlePresentation(id, outcome);
      this.dropFlow.startControlledDrop();
    }, (video) => this.audio.setObserverVideo(video));
    this.bridge = new SceneBridge(this.session, this.bodies, {
      onResult: (result) => this.handleSessionResult(result),
      onPresentation: (request) => this.presentation.play(request),
    });
    this.dropFlow = new DropFlowController({
      scene: this,
      bodies: this.bodies,
      bridge: this.bridge,
      getState: () => this.state,
      getStep: () => this.latestStep,
      getGameplayTimeMs: () => this.gameplayTimeMs,
      onPreviewMoved: (x) => this.controls?.syncX(x),
    });
    this.collisions = new CollisionCoordinator(
      this,
      this.bodies,
      (observation) => this.handlePhysicsStep(observation),
      (impact) => this.audio.collision(impact, this.time.now),
      () => this.beforePhysicsStep(),
    );
    this.controls = new InputController({
      scene: this,
      onMove: (x) => this.dropFlow.movePreview(x),
      onDrop: () => this.dropFlow.drop(),
      onPause: () => this.togglePause(),
      canControl: () => !this.autoplay?.enabled && this.dropFlow.canControl(),
    });
    this.autoplay = new AutoPlayController({
      getState: () => this.state,
      getBodies: () => this.bodies.all()
        .filter(body => body.entityId !== this.state.activeDropEntityId && body.image.visible)
        .map(({ entityId, characterId, body }) => ({
          entityId, characterId, x: body.position.x, y: body.position.y,
          speed: Math.hypot(body.velocity.x, body.velocity.y),
          parts: (body.parts.length > 1 ? body.parts.slice(1) : [body]).map(part => part.vertices ?? []),
        })),
      canControl: () => this.dropFlow.canControl(),
      dropAt: x => this.dropFlow.dropAt(x),
      useCard: request => this.pendingCardUses.push(request),
      onEnabled: enabled => runtimeBus.sendAuto(enabled),
    });
    runtimeBus.sendAuto(false);
    this.unsubscribeUi = runtimeBus.onUi((action) => this.handleUiAction(action));
    if (import.meta.env.DEV) {
      const debugLoadGeneration = ++this.debugLoadGeneration;
      void import('../../debug/installGameSceneDebug').then(({ installGameSceneDebug }) => {
        if (!this.sys.isActive() || this.debugLoadGeneration !== debugLoadGeneration) return;
        this.uninstallDebug = installGameSceneDebug({
          scene: this,
          session: this.session,
          bridge: this.bridge,
          bodies: this.bodies,
          dropFlow: this.dropFlow,
          presentation: this.presentation,
          getState: () => this.state,
          getLatestStep: () => this.latestStep,
          advanceStep: (amount) => {
            this.latestStep += amount;
            return this.latestStep;
          },
          setGameplayTimeMs: (nowMs) => {
            this.gameplayTimeMs = nowMs;
          },
          reset: (seed) => this.restartSession(seed, false),
          clearRecord: () => {
            this.record = this.recordStore.clear();
            runtimeBus.sendRecord(this.record);
          },
        });
      });
    }
    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('blur', this.handleWindowBlur);
    window.addEventListener('focus', this.handleWindowFocus);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
    this.bridge.accept({ state: this.state, commands: [], events: [] });
  }

  public override update(_time: number, delta: number): void {
    if (!this.economyModal) this.autoplay.update(this.gameplayTimeMs);
    this.syncRenderCamera(this.state.status === 'playing' ? delta : 0);
    const state = this.state;
    if (state.status === 'playing' && this.dropFlow.canControl()) this.dropFlow.updateGuide();
  }

  private handlePhysicsStep(observation: PhysicsStepObservation): void {
    this.latestStep = observation.step;
    if (this.state.status === 'playing') this.gameplayTimeMs += FIXED_STEP_MS;
    const gameplayObservation = { ...observation, nowMs: this.gameplayTimeMs };
    this.bridge.physics(gameplayObservation);
  }

  private handleSessionResult(result: SessionResult): void {
    const { state, events } = result;
    this.state = state;
    if (this.economyModal && state.status === 'playing') {
      this.bridge.pause();
      this.matter.world.pause();
      return;
    }
    if (this.economyModal && state.status !== 'presenting') this.game.loop.sleep();
    if (events.some((event) => event.type === 'phase-changed')) this.framing.reset();
    if (state.status === 'presenting') this.matter.world.pause();
    if (state.status === 'playing' && !this.focusSuspended && !document.hidden) this.matter.world.resume();
    const gameOver = events.find((event): event is Extract<SessionEvent, { type: 'game-over' }> => event.type === 'game-over');
    if (events.length > 0) this.persistRecord(state);
    if (gameOver) {
      this.autoplay.setEnabled(false);
      this.presentation.playGameOver(() => this.state.status === 'game-over');
    }
    runtimeBus.sendState(state, events);
    this.audio.handleEvents(events, state.phase, state.mode);
    this.audio.syncBgm(state);
    // This can synchronously create the next drop and publish a newer state.
    // Finish publishing this result first so it cannot overwrite that state.
    this.dropFlow.handleResult(events);
  }

  private handleUiAction(action: UiAction): void {
    if (this.economyModal && ['start', 'restart', 'auto-toggle', 'pause-toggle', 'card-use', 'select-mode', 'mode-menu'].includes(action.type)) return;
    switch (action.type) {
      case 'select-mode':
        if (this.state.status === 'ready' && this.state.mode !== action.mode) this.returnToModeMenu(action.mode);
        break;
      case 'mode-menu':
        if (this.state.status === 'paused' || this.state.status === 'game-over') this.returnToModeMenu(this.state.mode);
        break;
      case 'economy-modal':
        if (action.open && !this.economyModal) {
          this.economyModal = true;
          this.audio.unlock();
          this.economyResumeGeneration = this.state.status === 'playing' || this.state.status === 'presenting' ? this.state.generation : null;
          this.pendingCardUses.splice(0);
          if (this.state.status === 'playing') this.bridge.pause();
          this.matter.world.pause();
          if (this.state.status !== 'presenting') this.game.loop.sleep();
        } else if (!action.open && this.economyModal) {
          this.economyModal = false;
          if (!this.focusSuspended && !document.hidden) { this.game.loop.wake(); this.resumeEconomy(); }
        }
        break;
      case 'auto-toggle':
        this.audio.unlock();
        this.audio.play('button', 0.5);
        this.pendingCardUses.splice(0);
        this.autoplay.setEnabled(!this.autoplay.enabled);
        if (this.autoplay.enabled) {
          this.dropFlow.setContinuousDrop(true);
          if (this.state.status === 'ready') {
            this.matter.world.resume();
            this.bridge.start();
          } else if (this.state.status === 'game-over') {
            this.restartSession((this.state.seed + this.state.generation * 0x9e37_79b9) >>> 0, true);
          }
          this.dropFlow.startControlledDrop();
        }
        break;
      case 'start':
        if (action.mode && action.mode !== this.state.mode) this.returnToModeMenu(action.mode);
        this.audio.unlock();
        this.audio.play('button', 0.5);
        this.matter.world.resume();
        this.dropFlow.setContinuousDrop(true);
        this.bridge.start();
        this.dropFlow.startControlledDrop();
        break;
      case 'pause-toggle':
        this.audio.play('button', 0.45);
        this.togglePause();
        break;
      case 'restart': {
        this.audio.play('button', 0.5);
        const state = this.state;
        this.restartSession((state.seed + state.generation * 0x9e37_79b9) >>> 0, true);
        break;
      }
      case 'card-use':
        if (this.autoplay.enabled) break;
        this.pendingCardUses.push({
          generation: action.generation,
          instanceId: action.instanceId,
          targetEntityId: action.targetEntityId,
        });
        break;
      case 'mute-toggle':
        this.audio.toggleMute();
        break;
      case 'volume':
        this.audio.setVolume(action.value);
        break;
    }
  }

  private togglePause(): void {
    if (this.economyModal) return;
    const state = this.state;
    if (state.status === 'playing') {
      this.pendingCardUses.splice(0);
      this.bridge.pause();
      this.matter.world.pause();
    } else if (state.status === 'paused') {
      this.matter.world.resume();
      this.bridge.resume();
    }
  }

  private syncRenderCamera(delta = 0): void {
    const camera = this.cameras.main;
    const preview = this.dropFlow?.previewSnapshot();
    const previewTop = preview?.visible ? preview.y - preview.displayHeight * preview.originY : FIELD_HEIGHT;
    const top = delta > 0
      ? this.framing.update(Math.min(this.bodies?.visibleTop() ?? FIELD_HEIGHT, previewTop), delta)
      : this.framing.top;
    const height = VIEW_HEIGHT;
    const width = VIEW_WIDTH;
    const left = -VIEW_SIDE;
    camera.setZoom(RENDER_RESOLUTION_SCALE);
    camera.setScroll(FIELD_WIDTH / 2 - camera.width / 2, top + height / 2 - camera.height / 2);
    const host = document.querySelector<HTMLElement>('.playfield-frame');
    if (!host) return;
    host.style.aspectRatio = VIEW_WIDTH + ' / ' + VIEW_HEIGHT;
    const viewAspect = String(VIEW_WIDTH / VIEW_HEIGHT);
    host.style.setProperty('--view-aspect', viewAspect);
    host.closest<HTMLElement>('.game-layout')?.style.setProperty('--playfield-aspect', viewAspect);
    host.style.setProperty('--field-left', (-left / width * 100) + '%');
    // The physical floor, felt and rails share the same world-to-screen offset.
    host.dataset.cameraTop = String(top);
    host.style.setProperty('--field-top', (-top / height * 100) + '%');
    host.style.setProperty('--field-size', (FIELD_WIDTH / width * 100) + '%');
    host.style.setProperty('--field-height', (FIELD_HEIGHT / height * 100) + '%');
    host.style.setProperty('--rail-size', (24 / width * 100) + '%');
    host.style.setProperty('--rail-height', (24 / height * 100) + '%');
  }

  private restartSession(seed: number, continuousDropEnabled: boolean): void {
    this.autoplay.reset();
    this.pendingCardUses.splice(0);
    this.framing.reset();
    this.dropFlow.reset(continuousDropEnabled);
    this.presentation.abort(false);
    this.matter.world.resume();
    this.persistRecord(this.state);
    this.bridge.restart(seed);
    this.gameplayTimeMs = 0;
    this.publishDangerLevel(0);
    this.bridge.start();
    this.dropFlow.startControlledDrop();
  }

  private returnToModeMenu(mode: GameModeId): void {
    this.persistRecord(this.state);
    this.autoplay.setEnabled(false);
    this.resumeAutoAfterFocus = false;
    this.autoplay.reset();
    this.pendingCardUses.splice(0);
    this.framing.reset();
    this.dropFlow.reset(false);
    this.presentation.abort(false);
    this.matter.world.pause();
    this.gameplayTimeMs = 0;
    this.recordStore = new RecordStore(window.localStorage, mode);
    this.record = this.recordStore.load();
    this.bridge.restart((this.state.seed + this.state.generation * 0x9e37_79b9) >>> 0, mode, this.record.observerReached);
    runtimeBus.sendRecord(this.record);
    this.publishDangerLevel(0);
  }

  private persistRecord(state: SessionResult['state']): void {
    if (
      state.score <= this.record.highScore &&
      state.highestStage <= this.record.highestStage &&
      state.maxChain <= this.record.maxChain &&
      state.phase <= this.record.highestPhase &&
      (!state.observerReached || this.record.observerReached)
    ) return;
    this.record = this.recordStore.save({
      highScore: state.score,
      highestStage: state.highestStage,
      maxChain: state.maxChain,
      highestPhase: state.phase,
      observerReached: state.observerReached,
    });
    runtimeBus.sendRecord(this.record);
  }

  private publishDangerLevel(warningElapsedMs: number): void {
    const next: DangerLevel = warningElapsedMs <= 0
      ? 'safe'
      : warningElapsedMs >= DANGER_DURATION_MS * 0.6
        ? 'critical'
        : 'warning';
    if (next === this.dangerLevel) return;
    this.dangerLevel = next;
    runtimeBus.sendDanger(next);
  }

  private readonly handleVisibility = (): void => {
    if (document.hidden) {
      this.suspendForFocusLoss();
    } else {
      this.resumeAfterFocusGain();
    }
  };

  private readonly handleWindowBlur = (): void => {
    this.suspendForFocusLoss();
  };

  private readonly handleWindowFocus = (): void => {
    if (!document.hidden) this.resumeAfterFocusGain();
  };

  private suspendForFocusLoss(): void {
    if (this.focusSuspended) return;
    this.focusSuspended = true;
    this.resumeAutoAfterFocus = this.autoplay.enabled && this.state.status === 'playing';
    if (this.state.status === 'playing') this.togglePause();
    else this.matter.world.pause();
    this.game.loop.sleep();
  }

  private resumeAfterFocusGain(): void {
    if (!this.focusSuspended) return;
    this.focusSuspended = false;
    this.game.loop.wake();
    if (this.economyModal) { this.matter.world.pause(); if (this.state.status !== 'presenting') this.game.loop.sleep(); return; }
    this.resumeEconomy();
    if (this.resumeAutoAfterFocus && this.autoplay.enabled && this.state.status === 'paused') this.togglePause();
    this.resumeAutoAfterFocus = false;
    if (this.state.status !== 'paused' && this.state.status !== 'game-over') {
      this.matter.world.resume();
    }
  }

  private shutdown(): void {
    this.autoplay.setEnabled(false);
    this.audio.destroy();
    this.debugLoadGeneration += 1;
    this.unsubscribeUi?.();
    this.unsubscribeUi = null;
    this.uninstallDebug?.();
    this.uninstallDebug = null;
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('blur', this.handleWindowBlur);
    window.removeEventListener('focus', this.handleWindowFocus);
    this.controls.destroy();
    this.collisions.destroy();
    this.dropFlow.destroy();
    this.presentation.destroy();
    this.bodies.clear();
  }

  private resumeEconomy(): void {
    const generation = this.economyResumeGeneration;
    this.economyResumeGeneration = null;
    if (generation !== this.state.generation || this.state.status !== 'paused') return;
    this.bridge.resume();
    this.matter.world.resume();
    this.dropFlow.startControlledDrop();
  }

  private beforePhysicsStep(): void {
    this.processCardUses();
    if (this.state.status !== 'playing') return;
    const effects = this.state.cardEffects;
    this.dropFlow.maintainControlledFall();
    if (effects.buoyancy) this.bodies.applyBuoyancy(effects.buoyancy.targetEntityId);
    if (effects.attraction) this.bodies.applyAttraction(effects.attraction.sourceEntityId, effects.attraction.targetEntityId);
  }

  private processCardUses(): void {
    if (this.pendingCardUses.length === 0) return;
    const requests = this.pendingCardUses.splice(0);
    for (const request of requests) {
      if (this.state.status !== 'playing') break;
      this.bridge.useCard(request, this.prepareCardUse(request), this.gameplayTimeMs);
    }
  }

  private prepareCardUse(request: CardUseRequest): CardUsePreparation {
    const held = this.state.card.hand.find((entry) => entry.instanceId === request.instanceId);
    if (!held) return {};
    const cardId = held.cardId;
    if (cardId === 'swap') {
      return {
        replacementCandidates: request.targetEntityId
          ? this.bodies.safeReplacementCandidates(request.targetEntityId, [this.state.dropView.currentCharacterId])
          : [],
      };
    }
    if (cardId === 'reincarnate') {
      const ids = this.dropCandidates();
      return {
        replacementCandidates: request.targetEntityId ? this.bodies.safeReplacementCandidates(request.targetEntityId, ids) : [],
      };
    }
    if (cardId === 'reinforcement') {
      const reinforcementY: Record<string, number> = {};
      const reinforcementPositions: Record<string, readonly number[]> = {};
      this.dropCandidates().forEach(id => {
        reinforcementY[id] = this.bodies.dropY(id);
        reinforcementPositions[id] = this.bodies.safeDropPositions(id, reinforcementY[id]);
      });
      return { reinforcementPositions, reinforcementY };
    }
    return {};
  }

  private dropCandidates() {
    const { firstStage } = getModePhase(this.state.mode, this.state.phase);
    return getGameMode(this.state.mode).dropCounts.map((_, offset) => getCharacterAtStage(firstStage + offset).id);
  }
}

function readSeed(): number {
  const requested = new URLSearchParams(window.location.search).get('seed');
  if (requested && /^\d+$/.test(requested)) return Number(requested) >>> 0;
  const values = new Uint32Array(1);
  window.crypto.getRandomValues(values);
  return values[0] ?? 0x1f2e_3d4c;
}
